import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

// SPECIFIKACE.md ch. 7: the app has no user accounts. One shared link carries an invite token in
// its query string (`?invite=<token>`); the redeem-invite edge function swaps that token for a
// long-lived JWT (see supabase/functions/redeem-invite), which lives in localStorage from then on.
// Without it the app can only read the public tables anonymously (see cvAuth() in Api.kt).

// ---------------------------------------------------------------------------------------------
// localStorage
// ---------------------------------------------------------------------------------------------

private const val SESSION_TOKEN_KEY = "cv_session_token"
private const val DEFAULT_PARTICIPANT_KEY = "cv_default_participant_id"
private const val ADMIN_UNLOCKED_KEY = "cv_admin_unlocked"

private fun readLocal(key: String): String? =
    try { localStorage.getItem(key) } catch (_: Throwable) { null }

/** Writes, or removes when [value] is null. Storage can be disabled (private mode); the app then
 *  simply forgets its choices between visits instead of failing. */
private fun writeLocal(key: String, value: String?) {
    try {
        if (value == null) localStorage.removeItem(key) else localStorage.setItem(key, value)
    } catch (_: Throwable) {
    }
}

/** The write-capable JWT from redeem-invite. Expired tokens are dropped rather than returned, so
 *  a stale one sends the user back to the shared link instead of into a wall of 401s. */
fun loadSessionToken(): String? = readLocal(SESSION_TOKEN_KEY)?.takeUnless { isTokenExpired(it) }

fun saveSessionToken(token: String?) = writeLocal(SESSION_TOKEN_KEY, token)

/** Who this device writes for by default (SPECIFIKACE.md ch. 8 point 1). */
fun loadDefaultParticipantId(): String? = readLocal(DEFAULT_PARTICIPANT_KEY)

fun saveDefaultParticipantId(id: String?) = writeLocal(DEFAULT_PARTICIPANT_KEY, id)

/** Whether the admin screen's password gate has already been passed on this device. */
fun loadAdminUnlocked(): Boolean = readLocal(ADMIN_UNLOCKED_KEY) == "1"

fun saveAdminUnlocked(unlocked: Boolean) = writeLocal(ADMIN_UNLOCKED_KEY, if (unlocked) "1" else null)

// ---------------------------------------------------------------------------------------------
// JWT inspection
// ---------------------------------------------------------------------------------------------

@Serializable
data class JwtClaims(
    val role: String? = null,
    @SerialName("app_role") val appRole: String? = null,
    val exp: Long? = null,
)

/** `atob` is a browser global (base64 -> binary string); declared external for Kotlin/JS interop. */
external fun atob(data: String): String

private val jwtJson = Json { ignoreUnknownKeys = true }

/**
 * Reads the JWT payload client-side to check expiry. Not a security boundary -- the server's RLS
 * enforces permissions from the same claims, this only spares the user pointless failed requests.
 */
fun decodeJwtClaims(token: String): JwtClaims? = try {
    val payload = token.split(".").getOrNull(1) ?: return null
    var normalized = payload.replace('-', '+').replace('_', '/')
    normalized += "=".repeat((4 - normalized.length % 4) % 4)
    jwtJson.decodeFromString<JwtClaims>(atob(normalized))
} catch (_: Throwable) {
    null
}

/** True when the token carries an `exp` claim that has already passed (with a little slack). */
fun isTokenExpired(token: String, skewSeconds: Long = 10): Boolean {
    val exp = decodeJwtClaims(token)?.exp ?: return false
    val nowSeconds = (kotlin.js.Date().getTime() / 1000).toLong()
    return nowSeconds >= exp - skewSeconds
}

// ---------------------------------------------------------------------------------------------
// Invite token in the URL (the shared link's `?invite=<token>`)
// ---------------------------------------------------------------------------------------------

/** Reads the `invite` query parameter from the current URL, if the link carried one. */
fun inviteTokenFromUrl(): String? {
    val search = window.location.search.removePrefix("?")
    if (search.isBlank()) return null
    return search.split("&")
        .map { it.substringBefore('=') to it.substringAfter('=', "") }
        .firstOrNull { (key, value) -> key == "invite" && value.isNotBlank() }
        ?.second
}

/** Strips `invite` from the address bar once redeemed, so it does not linger in history or a
 *  screenshot. */
fun clearInviteFromUrl() {
    try {
        window.history.replaceState(null, "", window.location.pathname)
    } catch (_: Throwable) {
    }
}
